import React, { createContext, useContext, ReactNode } from 'react';

interface RadioGroupContextType {
  value: string;
  onChange: (value: string) => void;
}

const RadioGroupContext = createContext<RadioGroupContextType | undefined>(undefined);

interface RadioGroupProps {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  className?: string;
}

interface RadioGroupOptionProps {
  value: string;
  disabled?: boolean;
  className?: string | ((props: { checked: boolean }) => string);
  children: (props: { checked: boolean }) => ReactNode;
}

interface RadioGroupLabelProps {
  className?: string;
  children: ReactNode;
  as?: string;
}

interface RadioGroupDescriptionProps {
  className?: string;
  children: ReactNode;
  as?: string;
}

export function RadioGroup({ value, onChange, children, className = '' }: RadioGroupProps) {
  return (
    <RadioGroupContext.Provider value={{ value, onChange }}>
      <div role="radiogroup" className={className}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

function Option({ value, disabled = false, className = '', children }: RadioGroupOptionProps) {
  const context = useContext(RadioGroupContext);

  if (!context) {
    throw new Error('RadioGroup.Option must be used within a RadioGroup');
  }

  const { value: selectedValue, onChange } = context;
  const checked = selectedValue === value;

  const handleClick = () => {
    if (!disabled) {
      onChange(value);
    }
  };

  const resolvedClassName = typeof className === 'function' ? className({ checked }) : className;

  return (
    <div
      className={resolvedClassName}
      onClick={handleClick}
      role="radio"
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {children({ checked })}
    </div>
  );
}

function Label({ className = '', children, as = 'div' }: RadioGroupLabelProps) {
  const Component = as as keyof JSX.IntrinsicElements;
  return (
    <Component className={className}>
      {children}
    </Component>
  );
}

function Description({ className = '', children, as = 'div' }: RadioGroupDescriptionProps) {
  const Component = as as keyof JSX.IntrinsicElements;
  return (
    <Component className={className}>
      {children}
    </Component>
  );
}

// Attach the subcomponents to the RadioGroup component
RadioGroup.Option = Option;
RadioGroup.Label = Label;
RadioGroup.Description = Description;

export default RadioGroup;
